import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updatePhoto: (data) => api.put('/auth/photo', data),
  changePassword: (data) => api.put('/auth/password', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Photo Upload
export const uploadAPI = {
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadPhotoBase64: (imageData) => api.post('/upload/photo/base64', { image: imageData }),
};

// Notifications
export const notificationAPI = {
  list: () => api.get('/notifications'),
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
};

// Leaderboards
export const leaderboardAPI = {
  getByCoins: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/coins?limit=${limit}`),
  getByXP: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/xp?limit=${limit}`),
  getByLevel: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/level?limit=${limit}`),
};

// Analytics
export const analyticsAPI = {
  getClassAnalytics: (classId, days = 30, startDate = null, endDate = null) => {
    let url = `/classes/${classId}/analytics?days=${days}`;
    if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
    return api.get(url);
  },
  getStudentEngagement: (classId) => api.get(`/classes/${classId}/student-engagement`),
  getLeaderboardCoins: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/coins?limit=${limit}`),
  getLeaderboardXP: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/xp?limit=${limit}`),
  getLeaderboardLevel: (classId, limit = 10) => api.get(`/classes/${classId}/leaderboard/level?limit=${limit}`),
};

// Admin
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getProfessors: (status = null) => api.get(`/admin/professors${status ? `?status=${status}` : ''}`),
  getStudents: () => api.get('/admin/students'),
  approveUser: (userId) => api.put(`/admin/users/${userId}/approve`),
  pauseUser: (userId) => api.put(`/admin/users/${userId}/pause`),
  activateUser: (userId) => api.put(`/admin/users/${userId}/activate`),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  bulkDeleteUsers: (userIds) => api.post('/admin/users/bulk-delete', userIds),
  bulkActionUsers: (userIds, action) => api.post(`/admin/users/bulk-action?action=${action}`, userIds),
  getClasses: (professorId = null) => api.get(`/admin/classes${professorId ? `?professor_id=${professorId}` : ''}`),
  getClassStudents: (classId) => api.get(`/admin/classes/${classId}/students`),
  deleteClass: (classId) => api.delete(`/admin/classes/${classId}`),
  bulkDeleteClasses: (classIds) => api.post('/admin/classes/bulk-delete', classIds),
  sendResetLink: (userId) => api.post(`/admin/users/${userId}/send-reset-link`),
  sendAccessEmail: (userId) => api.post(`/admin/users/${userId}/send-access-email`),
};

// Classes
export const classAPI = {
  create: (data) => api.post('/classes', data),
  list: (includeArchived = false) => api.get(`/classes${includeArchived ? '?include_archived=true' : ''}`),
  get: (id) => api.get(`/classes/${id}`),
  update: (id, data) => api.put(`/classes/${id}`, data),
  join: (data) => api.post('/classes/join', data),
  getStudents: (classId) => api.get(`/classes/${classId}/students`),
  getStudentProfile: (classId, studentUserId) => api.get(`/classes/${classId}/students/${studentUserId}/profile`),
  getStats: (classId) => api.get(`/classes/${classId}/stats`),
  getActivityFeed: (classId) => api.get(`/classes/${classId}/activity-feed`),
  archive: (classId) => api.put(`/classes/${classId}/archive`),
  unarchive: (classId) => api.put(`/classes/${classId}/unarchive`),
  delete: (classId) => api.delete(`/classes/${classId}`),
  inviteByEmail: (classId, email) => api.post(`/classes/${classId}/invite`, { email }),
  getGroupHistory: (classId, groupId) => api.get(`/classes/${classId}/groups/${groupId}/history`),
  removeStudent: (classId, studentUserId) => api.delete(`/classes/${classId}/students/${studentUserId}`),
  clone: (classId) => api.post(`/classes/${classId}/clone`),
  // Assistants
  listAssistants: (classId) => api.get(`/classes/${classId}/assistants`),
  addAssistant: (classId, data) => api.post(`/classes/${classId}/assistants`, data),
  removeAssistant: (classId, userId) => api.delete(`/classes/${classId}/assistants/${userId}`),
  searchEligibleAssistants: (classId, q = '') => api.get(`/classes/${classId}/assistants/search?q=${encodeURIComponent(q)}`),
};

// Learning Objectives
export const objectiveAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/objectives`, data),
  list: (classId) => api.get(`/classes/${classId}/objectives`),
  update: (classId, objectiveId, data) => api.put(`/classes/${classId}/objectives/${objectiveId}`, data),
  delete: (classId, objectiveId) => api.delete(`/classes/${classId}/objectives/${objectiveId}`),
};

// Achievements
export const achievementAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/achievements`, data),
  list: (classId) => api.get(`/classes/${classId}/achievements`),
  update: (classId, achievementId, data) => api.put(`/classes/${classId}/achievements/${achievementId}`, data),
  delete: (classId, achievementId) => api.delete(`/classes/${classId}/achievements/${achievementId}`),
};

// Gamification
export const gamificationAPI = {
  getProgressSummary: (classId) => api.get(`/classes/${classId}/gamification/progress-summary`),
  getLevelSystem: (classId) => api.get(`/classes/${classId}/gamification/level-system`),
  getAchievementsProgress: (classId, studentId) => api.get(`/classes/${classId}/gamification/achievements-progress${studentId ? `?student_id=${studentId}` : ''}`),
  getStudentDashboard: (classId) => api.get(`/classes/${classId}/gamification/dashboard`),
};

// Activities
export const activityAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/activities`, data),
  list: (classId) => api.get(`/classes/${classId}/activities`),
  update: (classId, activityId, data) => api.put(`/classes/${classId}/activities/${activityId}`, data),
  delete: (classId, activityId) => api.delete(`/classes/${classId}/activities/${activityId}`),
  award: (classId, activityId, data) => api.post(`/classes/${classId}/activities/${activityId}/award`, data),
  subscribe: (classId, activityId) => api.post(`/classes/${classId}/activities/${activityId}/subscribe`),
  unsubscribe: (classId, activityId) => api.delete(`/classes/${classId}/activities/${activityId}/subscribe`),
  getSubscriptions: (classId) => api.get(`/classes/${classId}/activities/subscriptions`),
  getSubscribers: (classId, activityId) => api.get(`/classes/${classId}/activities/${activityId}/subscribers`),
  getCalendar: (classId, month, year) => api.get(`/classes/${classId}/activities/calendar?month=${month}&year=${year}`),
  getCompletionsSummary: (classId) => api.get(`/classes/${classId}/activities/completions-summary`),
  getCompletionsHistory: (classId) => api.get(`/classes/${classId}/activities/completions-history`),
  deleteCompletion: (classId, completionId) => api.delete(`/classes/${classId}/activities/completions/${completionId}`),
  // Activity Status System
  updateStatus: (classId, activityId, status) => api.put(`/classes/${classId}/activities/${activityId}/status`, { status }),
  getStatuses: (classId, activityId) => api.get(`/classes/${classId}/activities/${activityId}/statuses`),
  getMyActivitiesStatus: (classId) => api.get(`/classes/${classId}/my-activities-status`),
  getMyRejectionFeedback: (classId) => api.get(`/classes/${classId}/my-rejection-feedback`),
  updateStudentStatus: (classId, activityId, studentId, status) => 
    api.put(`/classes/${classId}/activities/${activityId}/student/${studentId}/status`, { status }),
  // Batch award
  awardBatch: (classId, activityId, studentIds, note) => 
    api.post(`/classes/${classId}/activities/${activityId}/award-batch`, { student_user_ids: studentIds, note }),
  // Completion counts for recurring activities
  getCompletionCounts: (classId, activityId) => 
    api.get(`/classes/${classId}/activities/${activityId}/completion-counts`),
  // Approve/reject student completion
  approveCompletion: (classId, activityId, studentId, action, rejectionNote = '') =>
    api.post(`/classes/${classId}/activities/${activityId}/approve/${studentId}`, { action, rejection_note: rejectionNote }),
  // Pending activity approvals for professor
  getPendingActivityApprovals: (classId) => api.get(`/classes/${classId}/activities/pending-approvals`),
  // Sync activity statuses
  syncActivityStatuses: (classId) => api.post(`/classes/${classId}/sync-activity-statuses`),
  // Student progress
  getStudentProgress: (classId) => api.get(`/classes/${classId}/student-progress`),
};

// Categories (Activity Categories)
export const categoryAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/categories`, data),
  list: (classId) => api.get(`/classes/${classId}/categories`),
  update: (classId, categoryId, data) => api.put(`/classes/${classId}/categories/${categoryId}`, data),
  delete: (classId, categoryId) => api.delete(`/classes/${classId}/categories/${categoryId}`),
};

// Wallet
export const walletAPI = {
  get: (classId) => api.get(`/classes/${classId}/wallet`),
  adjust: (classId, data) => api.post(`/classes/${classId}/wallet/adjust`, data),
};

// Student Dashboard
export const dashboardAPI = {
  getStudentDashboard: (classId) => api.get(`/classes/${classId}/gamification/dashboard`),
  getActivityFeed: (classId, limit = 30) => api.get(`/classes/${classId}/social-feed?limit=${limit}`),
};

// Products
export const productAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/products`, data),
  list: (classId) => api.get(`/classes/${classId}/products`),
  update: (classId, productId, data) => api.put(`/classes/${classId}/products/${productId}`, data),
  delete: (classId, productId) => api.delete(`/classes/${classId}/products/${productId}`),
  purchase: (classId, productId) => api.post(`/classes/${classId}/products/${productId}/purchase`),
  getPendingPurchases: (classId) => api.get(`/classes/${classId}/purchases/pending`),
  getAllPurchases: (classId, status = null) => api.get(`/classes/${classId}/purchases/all${status ? `?status=${status}` : ''}`),
  getMyPurchases: (classId) => api.get(`/classes/${classId}/purchases/my`),
  getPurchasesSummary: (classId) => api.get(`/classes/${classId}/purchases/summary`),
  approvePurchase: (classId, purchaseId) => api.post(`/classes/${classId}/purchases/${purchaseId}/approve`),
  rejectPurchase: (classId, purchaseId) => api.post(`/classes/${classId}/purchases/${purchaseId}/reject`),
};

// Marketplace Categories
export const marketplaceCategoryAPI = {
  create: (classId, name, color) => api.post(`/classes/${classId}/marketplace-categories?name=${encodeURIComponent(name)}&color=${encodeURIComponent(color)}`),
  list: (classId) => api.get(`/classes/${classId}/marketplace-categories`),
  delete: (classId, categoryId) => api.delete(`/classes/${classId}/marketplace-categories/${categoryId}`),
};

// Listings
export const listingAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/listings`, data),
  list: (classId, status = null) => api.get(`/classes/${classId}/listings${status ? `?status=${status}` : ''}`),
  myListings: (classId) => api.get(`/classes/${classId}/listings/my`),
  update: (classId, listingId, data) => api.put(`/classes/${classId}/listings/${listingId}`, data),
  delete: (classId, listingId) => api.delete(`/classes/${classId}/listings/${listingId}`),
  approve: (classId, listingId) => api.post(`/classes/${classId}/listings/${listingId}/approve`),
  reject: (classId, listingId) => api.post(`/classes/${classId}/listings/${listingId}/reject`),
  purchase: (classId, listingId) => api.post(`/classes/${classId}/listings/${listingId}/purchase`),
  interest: (classId, listingId, data) => api.post(`/classes/${classId}/listings/${listingId}/interest`, data),
  getInterests: (classId, listingId) => api.get(`/classes/${classId}/listings/${listingId}/interests`),
};

// Groups
export const groupAPI = {
  create: (classId, data) => api.post(`/classes/${classId}/groups`, data),
  list: (classId, includeMembers = false) => api.get(`/classes/${classId}/groups?include_members=${includeMembers}`),
  get: (classId, groupId) => api.get(`/classes/${classId}/groups/${groupId}`),
  update: (classId, groupId, data) => api.put(`/classes/${classId}/groups/${groupId}`, data),
  delete: (classId, groupId) => api.delete(`/classes/${classId}/groups/${groupId}`),
  addMembers: (classId, groupId, studentUserIds) => api.post(`/classes/${classId}/groups/${groupId}/members`, { student_user_ids: studentUserIds }),
  removeMembers: (classId, groupId, studentUserIds) => api.delete(`/classes/${classId}/groups/${groupId}/members`, { data: { student_user_ids: studentUserIds } }),
  getMembers: (classId, groupId) => api.get(`/classes/${classId}/groups/${groupId}/members`),
  getUnassignedStudents: (classId, search = '') => api.get(`/classes/${classId}/unassigned-students?search=${encodeURIComponent(search)}`),
  getStudentGroups: (classId, studentId) => api.get(`/classes/${classId}/students/${studentId}/groups`),
  getMyGroups: (classId) => api.get(`/classes/${classId}/my-groups`),
  getLeaderboard: (classId, type = 'coins') => api.get(`/classes/${classId}/leaderboard/groups?type=${type}`),
  awardActivity: (classId, activityId, groupId, note = '') => 
    api.post(`/classes/${classId}/activities/${activityId}/award-group`, { group_id: groupId, note }),
  // Leader
  setLeader: (classId, groupId, leaderUserId) => api.put(`/classes/${classId}/groups/${groupId}/leader`, { leader_user_id: leaderUserId }),
  removeLeader: (classId, groupId) => api.delete(`/classes/${classId}/groups/${groupId}/leader`),
  // Messages
  getMessages: (classId, groupId, limit = 50, before = null) => {
    let url = `/classes/${classId}/groups/${groupId}/messages?limit=${limit}`;
    if (before) url += `&before=${before}`;
    return api.get(url);
  },
  sendMessage: (classId, groupId, content) => api.post(`/classes/${classId}/groups/${groupId}/messages`, { content }),
  uploadFile: (classId, groupId, formData) => api.post(`/classes/${classId}/groups/${groupId}/messages/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteMessage: (classId, groupId, messageId) => api.delete(`/classes/${classId}/groups/${groupId}/messages/${messageId}`),
  // Group Wallet
  getGroupWallet: (classId, groupId) => api.get(`/classes/${classId}/groups/${groupId}/wallet`),
  groupPurchase: (classId, groupId, productId) => api.post(`/classes/${classId}/groups/${groupId}/purchase/${productId}`),
};

// Direct Messaging
export const messagingAPI = {
  getConversations: (classId) => api.get(`/classes/${classId}/messages`),
  getMessages: (classId, otherUserId, limit = 50) => api.get(`/classes/${classId}/messages/${otherUserId}?limit=${limit}`),
  sendMessage: (classId, otherUserId, data) => api.post(`/classes/${classId}/messages/${otherUserId}`, data),
  uploadFile: (classId, otherUserId, formData) => api.post(`/classes/${classId}/messages/${otherUserId}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
};


export default api;
